/**
 * Guided Onboarding Tour
 * ======================
 * Interactive walkthrough for new users after verification.
 * Personalized based on industry, tier, and selected modules.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronRight, ChevronLeft, Check, Sparkles, 
  BarChart3, Users, Zap, FileText, Globe, Settings,
  Building2, TrendingUp, Shield, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '@/utils/authFetch';

const TOUR_STEPS = [{
    id: 'welcome', title: 'Welcome to Corvana!', description: 'Your AI-powered business intelligence platform is ready.', icon: Sparkles, highlight: null, action: null, personalizedContent: (user) => `
      Hi ${user?.first_name || 'there'}! We've set up your ${user?.company_name || 'business'} dashboard 
      with industry insights specifically for ${user?.industry?.replace(/_/g, ' ') || 'your industry'}.
    `
  }, {
    id: 'dashboard', title: 'Your Dashboard Overview', description: 'Track all your key metrics in one place.', icon: BarChart3, highlight: '[data-tour="dashboard-overview"]',
    personalizedContent: (user) => `
      Your dashboard is customized for ${user?.tier || 'your'} tier with 
      ${user?.modules?.length || 'all available'} modules activated.
    `
  },
  {
    id: 'integrations',
    title: 'Connect Your Data Sources',
    description: 'Link your accounting software to unlock powerful insights.',
    icon: Zap,
    highlight: '[data-tour="integrations"]',
    action: 'connect_accounting',
    personalizedContent: (user) => user?.accounting_connected 
      ? 'Great! Your accounting software is already connected. You can add more integrations anytime.'
      : 'Connect your accounting software (MYOB, Xero, QuickBooks) to start seeing real-time financial insights.'
  },
  {
    id: 'intelligence',
    title: 'Business Intelligence',
    description: 'Access AI-powered insights and industry benchmarks.',
    icon: TrendingUp,
    highlight: '[data-tour="intelligence"]',
    personalizedContent: (user) => `
      Compare your ${user?.industry?.replace(/_/g, ' ') || 'business'} performance against 
      industry benchmarks and get AI recommendations for growth.
    `
  },
  {
    id: 'operations',
    title: 'Operations & Team',
    description: 'Manage your team and daily operations.',
    icon: Users,
    highlight: '[data-tour="operations"]',
    cta: { label: 'Invite your first team member', path: '/team?invite=1' },
    personalizedContent: (user) => `
      You have ${user?.staff_count || '1'} team member(s) across ${user?.locations || '1'} location(s). 
      Add your team to unlock collaborative features.
    `
  },
  {
    id: 'compliance',
    title: 'Compliance & Documents',
    description: 'Stay on top of industry compliance requirements.',
    icon: Shield,
    highlight: '[data-tour="compliance"]',
    personalizedContent: (user) => `
      We track compliance requirements specific to ${user?.industry?.replace(/_/g, ' ') || 'your industry'} 
      in Australia. Upload your documents to get started.
    `
  },
  {
    id: 'settings',
    title: 'Customize Your Experience',
    description: 'Adjust settings and preferences to match your workflow.',
    icon: Settings,
    highlight: '[data-tour="settings"]',
    personalizedContent: () => 'Set up notifications, customize your dashboard layout, and configure team permissions.'
  },
  {
    id: 'complete',
    title: 'You\'re All Set!',
    description: 'Start exploring your personalized business intelligence platform.',
    icon: Check,
    highlight: null,
    action: 'complete_tour',
    personalizedContent: (user) => `
      Your 14-day trial is active. Explore all features and let us know if you need any help!
      ${user?.tier === 'enterprise' ? 'Your dedicated account manager will reach out within 24 hours.' : ''}
    `
  }
];

export function GuidedOnboardingTour({ onComplete, onSkip }) {
  const { user} = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [completedSteps, setCompletedSteps] = useState([]);

  const step = TOUR_STEPS[currentStep];
  const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

  // Highlight current element
  useEffect(() => {
    if (step?.highlight) {
      const element = document.querySelector(step.highlight);
      if (element) {
        element.classList.add('tour-highlight');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        return () => {
          element.classList.remove('tour-highlight');
        };
      }
    }
  }, [step]);

  const handleNext = useCallback(() => {
    setCompletedSteps(prev => [...prev, step.id]);
    
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tour complete
      handleComplete();
    }
  // eslint-disable-next-line
  }, [currentStep, step]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleComplete = useCallback(async () => {
    // Mark tour as completed in backend (auth via HttpOnly cookie).
    try {
      await authFetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }

    setIsVisible(false);
    onComplete?.();
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    setIsVisible(false);
    onSkip?.();
  }, [onSkip]);

  const handleCta = useCallback((path) => {
    setCompletedSteps((prev) => (prev.includes(step.id) ? prev : [...prev, step.id]));
    setIsVisible(false);
    onSkip?.();
    navigate(path);
  }, [step, onSkip, navigate]);

  if (!isVisible) return null;

  const StepIcon = step.icon;

  return (
    <>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
        onClick={handleSkip}
      />

      {/* Tour Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-full max-w-lg"
        >
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Progress Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4">
              <div className="flex items-center justify-between text-white mb-2">
                <span className="text-sm font-medium">
                  Step {currentStep + 1} of {TOUR_STEPS.length}
                </span>
                <button
                  onClick={handleSkip}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <Progress value={progress} className="h-1.5 bg-white/20" />
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Icon */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                  <StepIcon className="w-7 h-7 text-indigo-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-800">{step.title}</h3>
                  <p className="text-sm text-slate-500">{step.description}</p>
                </div>
              </div>

              {/* Personalized Content */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <p className="text-slate-700 text-sm leading-relaxed">
                  {step.personalizedContent(user)}
                </p>
                {step.cta && (
                  <Button
                    onClick={() => handleCta(step.cta.path)}
                    data-testid="tour-cta-button"
                    className="mt-3 w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    {step.cta.label}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>

              {/* Step Indicators */}
              <div className="flex justify-center gap-1.5 mb-6">
                {TOUR_STEPS.map((s, index) => (
                  <button
                    key={s.id}
                    onClick={() => setCurrentStep(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentStep 
                        ? 'w-6 bg-indigo-600' 
                        : completedSteps.includes(s.id)
                          ? 'bg-green-500'
                          : 'bg-slate-200'
                    }`}
                  />
                ))}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={handlePrev}
                  disabled={currentStep === 0}
                  className="text-slate-600"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>

                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    onClick={handleSkip}
                    className="text-slate-500 text-sm"
                  >
                    Skip Tour
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white"
                  >
                    {currentStep === TOUR_STEPS.length - 1 ? (
                      <>
                        Get Started <Check className="w-4 h-4 ml-1" />
                      </>
                    ) : (
                      <>
                        Next <ChevronRight className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Tour Highlight Styles */}
      <style>{`
        .tour-highlight {
          position: relative;
          z-index: 102;
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3);
          border-radius: 8px;
          animation: pulse-highlight 2s ease-in-out infinite;
        }
        
        @keyframes pulse-highlight {
          0%, 100% { box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.5), 0 0 20px rgba(99, 102, 241, 0.3); }
          50% { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.4), 0 0 30px rgba(99, 102, 241, 0.4); }
        }
      `}</style>
    </>
  );
}

export default GuidedOnboardingTour;
